/**
 * Utility to format study and document titles according to specific rules:
 * 1. Remove initial numbers and underscores.
 * 2. Convert underscores to spaces.
 * 3. Keep acronyms (all caps) as is.
 * 4. Lowercase common prepositions/conjunctions.
 * 5. Capitalize the first letter of other words.
 */
export const formatStudyTitle = (str: string): string => {
    if (!str) return "";

    const exceptions = ['a','con','de','del','en','para','por','sin','tras','versus','el','la','los','las','un','una','unos','unas','al','y','e','ni','o','u'];
    const acronymRegex = /^[A-ZÁÉÍÓÚÑ]+$/; 

    // 1. Pre-cleanup: Replace underscores with spaces and handle initial numbers
    // Note: filenames often have 03_Acerca... while data points might have 03 Acerca...
    let cleanStr = str.replace(/_/g, ' ');
    
    // Remove leading numbers followed by space or underscore (e.g., "01 ", "01_") 
    // or just leading numbers if they are followed by text
    cleanStr = cleanStr.replace(/^\d+[\s_]*/, "");

    const words = cleanStr.trim().split(/\s+/).filter(w => w.length > 0);
    
    if (words.length === 0) return "";

    return words.map((w, i) => {
        // Remove .pdf or other small extensions from the word if it's the last word
        let wordToFormat = w;
        let suffix = "";
        if (i === words.length - 1 && w.toLowerCase().endsWith(".pdf")) {
            wordToFormat = w.slice(0, -4);
            suffix = ".pdf";
        }

        // Check if it's an acronym (all caps, >1 char)
        if (wordToFormat.length > 1 && acronymRegex.test(wordToFormat)) {
            return wordToFormat + suffix;
        }
        
        const lowerW = wordToFormat.toLowerCase();
        
        // If it's an exception and NOT the first word, return lowercase
        if (i > 0 && exceptions.includes(lowerW)) {
            return lowerW + suffix;
        }
        
        // Default: Capitalize first, lowercase the rest
        return (lowerW.charAt(0).toUpperCase() + lowerW.slice(1)) + suffix;
    }).join(' ');
};
